import React, { useSyncExternalStore } from 'react';

import { DocumentIcon } from '@heroicons/react/24/outline';
import styled, { keyframes } from 'styled-components';

import { OrderDesignFile } from '@/types/IOrderV2';

const PDFContainer = styled.div({
  height: '100%',
  paddingTop: '26px',
  borderTop: '1px solid #E5E7EB',
});

const IframeWrapper = styled.div({
  height: '100%',
  backgroundColor: '#F3F4F6',
  borderRadius: '8px',
  overflow: 'hidden',
  boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.05)',
});

const StyledIframe = styled.iframe({
  width: '100%',
  height: '100%',
  border: 'none',
});

const EmptyState = styled.div({
  padding: '40px',
  textAlign: 'center',
  color: '#6b7280',
  '& p:last-child': {
    fontSize: '14px',
    marginTop: '8px',
  },
});

const float = keyframes`
  0%, 100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-8px);
  }
`;

const shimmer = keyframes`
  0% {
    background-position: -200% center;
  }
  100% {
    background-position: 200% center;
  }
`;

const pulse = keyframes`
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.6;
  }
`;

const MobileFallbackContainer = styled.div`
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #fafafa;
  background-image: radial-gradient(
      circle at 20% 30%,
      rgba(101, 45, 191, 0.03) 0%,
      transparent 50%
    ),
    radial-gradient(
      circle at 80% 70%,
      rgba(101, 45, 191, 0.04) 0%,
      transparent 50%
    );
  padding: 24px;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-image: repeating-linear-gradient(
      0deg,
      transparent,
      transparent 2px,
      rgba(101, 45, 191, 0.015) 2px,
      rgba(101, 45, 191, 0.015) 4px
    );
    pointer-events: none;
  }
`;

const MobileFallbackCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 28px;
  padding: 48px 32px;
  background-color: #ffffff;
  border-radius: 16px;
  box-shadow: 0px 0px 0px 1px rgba(101, 45, 191, 0.08),
    0px 4px 12px rgba(0, 0, 0, 0.04), 0px 12px 32px rgba(101, 45, 191, 0.08);
  max-width: 400px;
  width: 100%;
  position: relative;
  z-index: 1;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(90deg, #652dbf 0%, #8b5cf6 50%, #652dbf 100%);
    background-size: 200% 100%;
    animation: ${shimmer} 3s linear infinite;
    border-top-left-radius: 16px;
    border-top-right-radius: 16px;
  }
`;

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 96px;
  height: 96px;
  background-color: rgba(101, 45, 191, 0.06);
  border-radius: 24px;
  position: relative;
  animation: ${float} 3s ease-in-out infinite;

  &::after {
    content: '';
    position: absolute;
    inset: -2px;
    border-radius: 24px;
    padding: 2px;
    background: linear-gradient(
      135deg,
      rgba(101, 45, 191, 0.2),
      rgba(139, 92, 246, 0.2)
    );
    -webkit-mask: linear-gradient(#fff 0 0) content-box,
      linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
    mask-composite: exclude;
    animation: ${pulse} 2s ease-in-out infinite;
  }
`;

const StyledDocumentIcon = styled(DocumentIcon)({
  width: '56px',
  height: '56px',
  color: '#652DBF',
  strokeWidth: 1.5,
});

const OpenButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 14px 32px;
  font-size: 15px;
  font-weight: 600;
  color: #ffffff;
  background-color: #652dbf;
  border: none;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0px 1px 2px rgba(101, 45, 191, 0.12),
    0px 4px 12px rgba(101, 45, 191, 0.16);
  position: relative;
  overflow: hidden;

  &:hover {
    background-color: #5a26a8;
    transform: translateY(-2px);
    box-shadow: 0px 2px 4px rgba(101, 45, 191, 0.16),
      0px 8px 20px rgba(101, 45, 191, 0.24);
  }

  &:active {
    transform: translateY(0px);
    box-shadow: 0px 1px 2px rgba(101, 45, 191, 0.12),
      0px 2px 8px rgba(101, 45, 191, 0.16);
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      135deg,
      rgba(255, 255, 255, 0.2) 0%,
      rgba(255, 255, 255, 0) 100%
    );
    opacity: 0;
    transition: opacity 0.2s;
  }

  &:hover::before {
    opacity: 1;
  }
`;

interface IPdfPreviewComponent {
  setError: React.Dispatch<React.SetStateAction<boolean>>;
  designFile?: OrderDesignFile;
}

const subscribe = (callback: () => void): (() => void) => {
  window.addEventListener('resize', callback);
  return () => window.removeEventListener('resize', callback);
};

const getSnapshot = (): boolean => window.innerWidth <= 768;

const getServerSnapshot = (): boolean => false;

const useMobileDetection = (): boolean => {
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
};

export const PdfPreView: React.FC<IPdfPreviewComponent> = ({
  setError,
  designFile,
}) => {
  const fileUrl = designFile?.original_url;
  const isMobile = useMobileDetection();

  const handleOpenPdf = (): void => {
    if (fileUrl) {
      window.open(fileUrl, '_blank', 'noopener,noreferrer');
    }
  };

  if (!fileUrl) {
    return (
      <PDFContainer>
        <EmptyState role="alert" aria-live="polite">
          <p>No PDF file specified.</p>
          <p>Please select a design file to preview.</p>
        </EmptyState>
      </PDFContainer>
    );
  }

  if (isMobile) {
    return (
      <PDFContainer>
        <MobileFallbackContainer>
          <MobileFallbackCard>
            <IconWrapper>
              <StyledDocumentIcon />
            </IconWrapper>
            <OpenButton onClick={handleOpenPdf} type="button">
              Open PDF
            </OpenButton>
          </MobileFallbackCard>
        </MobileFallbackContainer>
      </PDFContainer>
    );
  }

  return (
    <PDFContainer>
      <IframeWrapper>
        <StyledIframe
          src={fileUrl}
          title="Design Proof Preview"
          onError={() => setError(true)}
        />
      </IframeWrapper>
    </PDFContainer>
  );
};

export default React.memo(PdfPreView);
